// useAudioPlayer.ts
import { trpc } from '@/App'
import showToastNotification from '@/hooks/useShowToast'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

// Constants for localStorage keys
const AUDIO_STATE_KEY = 'audioPlayerState'
const AUDIO_TRACKS_KEY = 'audioTracks'
const AUDIO_POPOUT_OPEN_KEY = 'audioPopoutOpen'

/**
 * Representation of a single audio track.
 * @property title   - The track's title.
 * @property artist  - The track's artist (optional).
 * @property url     - The URL to the audio file.
 * @property artwork - The URL or path to the track's artwork image.
 */
export type AudioTrack = {
  title: string
  artist?: string
  url: string
  artwork: string
}

/**
 * Shared state structure for audio playback.
 * This state is synced across tabs via localStorage.
 * @property trackIndex    - Index of the currently playing track in `tracks[]`.
 * @property trackProgress - Current playback time (in seconds) of the audio.
 * @property isPlaying     - Boolean flag indicating if audio is playing.
 */
interface SharedAudioState {
  trackIndex: number
  trackProgress: number
  isPlaying: boolean
}

/**
 * Custom hook encapsulating audio playback logic, including:
 *   - Shared state across tabs (index, progress, play/pause)
 *   - Track list management
 *   - Preloading of next track
 *   - Syncing playback progress across parent and popout windows
 *   - Fetching random tracks via TRPC
 *   - Notification of metadata changes (popout → parent)
 *   - Seamless preloading and swapping without repetition
 *   - Ensuring only one tab (parent or popout) plays audio at a time
 *
 * Internally optimized to reuse a single Audio element and to avoid
 * accumulating anonymous listeners on each load.
 */
export const useAudioPlayer = (isPopout: boolean = false) => {
  // --------------------------------------------------------------------------
  // 1) Refs & Initialization Flags
  // --------------------------------------------------------------------------

  // Reference to a container <div> for UI (parent component might attach to this)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Flag ref so that we only initialize popout-specific logic once
  const didInit = useRef(false)

  // --------------------------------------------------------------------------
  // 2) Shared Playback State (via localStorage)
  // --------------------------------------------------------------------------

  /**
   * We store trackIndex, trackProgress, and isPlaying in localStorage under key AUDIO_STATE_KEY.
   * Both parent and popout read/write to this. When one changes it, the other will see the update.
   */
  const [sharedState, setSharedState] = useLocalStorage<SharedAudioState>(AUDIO_STATE_KEY, {
    trackIndex: 0,
    trackProgress: 0,
    isPlaying: false,
  })
  // Destructure for convenience:
  const { trackIndex, trackProgress, isPlaying } = sharedState

  // --------------------------------------------------------------------------
  // 3) Shared Track List (via localStorage)
  // --------------------------------------------------------------------------

  /**
   * The array of AudioTrack objects is also shared in localStorage under key AUDIO_TRACKS_KEY.
   * The parent tab will fetch/populate tracks; the popout just reads from this array.
   */
  const [tracks, setTracks] = useLocalStorage<AudioTrack[]>(AUDIO_TRACKS_KEY, [])

  // --------------------------------------------------------------------------
  // 4) Popout-Open Flag (via localStorage)
  // --------------------------------------------------------------------------

  /**
   * A boolean flag stored under AUDIO_POPOUT_OPEN_KEY. When true, it indicates that
   * a popout window is open. The parent UI can respond (e.g., pause its own audio).
   */
  const [isPopoutOpen, setIsPopoutOpen] = useLocalStorage<boolean>(AUDIO_POPOUT_OPEN_KEY, false)

  // --------------------------------------------------------------------------
  // 5) Local UI State (loading & expanded)
  // --------------------------------------------------------------------------

  /**
   * isLoading: true when either we have no tracks at all yet, or while we are swapping/preloading.
   * isExpanded: UI state for whether the mini‐player is expanded (e.g., on hover or click).
   */
  const [isLoading, setIsLoading] = useState(tracks.length === 0)
  const [isExpanded, setIsExpanded] = useState(false)

  // --------------------------------------------------------------------------
  // 6) Refs for Actual Audio Playback and Timers
  // --------------------------------------------------------------------------

  /**
   * We now create exactly one HTMLAudioElement at initialization and reuse it always.
   * This avoids creating multiple `new Audio()` instances and accumulating listeners.
   */
  const audioRef = useRef<HTMLAudioElement>(new Audio())

  // Reference to the interval timer that updates trackProgress each second
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Reference to the timeout that auto-collapses the expanded UI after inactivity
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // --------------------------------------------------------------------------
  // 7) TRPC Query to Fetch a Random Track
  // --------------------------------------------------------------------------

  /**
   * We use TRPC to fetch a random track. The parent will do this when it needs a fresh track
   * (initial load or when at the end of the playlist). We disable auto‐refetching on window focus
   * or reconnect to avoid unwanted fetches.
   */
  const {
    data: randomMusic,
    refetch,
    isFetching,
  } = trpc.getRandomSoundTrack.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // --------------------------------------------------------------------------
  // 8) Derive Current Track Metadata
  // --------------------------------------------------------------------------

  // Safely grab the current AudioTrack object from the shared `tracks[]` array based on trackIndex
  const current = tracks[trackIndex] ?? null
  // Destructure to get title, artist, URL for audio, artwork URL
  const currentTitle = current?.title ?? ''
  const currentArtist = current?.artist ?? ''
  const audioSrc = current?.url ?? ''
  const currentArtwork = current?.artwork ?? '../../../assets/artwork.jpg'

  // We also read the `duration` from the single audioRef; it'll be NaN until metadata loads
  const duration = audioRef.current.duration

  // --------------------------------------------------------------------------
  // 9) Helper: Reset Auto-Hide Timer
  // --------------------------------------------------------------------------

  /**
   * Whenever the user interacts (hovers or scrubs), we want to keep the mini‐player UI expanded.
   * After 5 seconds of no interaction, we collapse it again. This function resets the 5s timer.
   */
  const resetHideTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setIsExpanded(false)
    }, 5000)
  }, [])

  // --------------------------------------------------------------------------
  // 10) Helper: Preload Audio Metadata
  // --------------------------------------------------------------------------

  /**
   * Given a URL, create a temporary <audio> element and wait for its 'loadedmetadata' event.
   * This ensures we know duration and can begin playback without initial buffering delay.
   * Returns a promise that resolves to the loaded <audio> element, or rejects if error.
   */
  const preloadAudio = (url: string): Promise<HTMLAudioElement> => {
    return new Promise((resolve, reject) => {
      const tmpAudio = new Audio()
      tmpAudio.src = url
      tmpAudio.addEventListener('loadedmetadata', () => {
        resolve(tmpAudio)
      })
      tmpAudio.addEventListener('error', (err) => {
        reject(err)
      })
    })
  }

  // --------------------------------------------------------------------------
  // 11) Determine “Active Player” Based on Popout State
  // --------------------------------------------------------------------------

  /**
   * If this hook is running inside the popout window (isPopout === true), then this window
   * is active only when isPopoutOpen === true. If this is the parent window (isPopout === false),
   * then the parent is active when no popout is open (i.e., !isPopoutOpen).
   */
  const isActivePlayer = isPopout ? isPopoutOpen : !isPopoutOpen

  // --------------------------------------------------------------------------
  // 12) Start Playback Timer (updates trackProgress & auto‐advance)
  // --------------------------------------------------------------------------

  /**
   * Once we begin playback (i.e., audioRef.current.play() has been called), we start a 1s interval:
   *   - If the audio has ended, call toNextTrack() to auto‐advance.
   *   - Otherwise, if this window is the active player, write audioRef.current.currentTime
   *     into sharedState.trackProgress so both parent and popout stay in sync.
   *
   * We ensure the previous interval is always cleared before starting a new one.
   */
  const startTimer: () => void = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    intervalRef.current = setInterval(() => {
      if (audioRef.current.ended) {
        toNextTrack()
      } else if (isActivePlayer) {
        const currentTime = audioRef.current.currentTime
        setSharedState((prev) => ({
          ...prev,
          trackProgress: currentTime,
        }))
      }
    }, 1000)
  }, [setSharedState, isActivePlayer])

  // --------------------------------------------------------------------------
  // 13) Advance to the Next Track
  // --------------------------------------------------------------------------

  /**
   * toNextTrack: Moves to trackIndex + 1. Two cases:
   *   A) nextIndex < tracks.length: the next track is already in the shared array.
   *      • Preload its metadata
   *      • Pause the current <audio>
   *      • Update sharedState.trackIndex → this triggers the `useEffect` below to load/play the new track
   *   B) nextIndex >= tracks.length: we are at the end, so fetch a fresh track via TRPC.
   *      • Preload the newly fetched URL
   *      • Append the new AudioTrack to tracks[]
   *      • Pause the current <audio>
   *      • Update sharedState.trackIndex → triggers the `useEffect` below to load/play the new track
   *
   * In both cases, we set sharedState.isPlaying = isActivePlayer so that only the active window actually plays.
   *
   * We guard against concurrent calls by checking isLoading and set it accordingly.
   */
  const toNextTrack = useCallback(async () => {
    if (isLoading) {
      return
    }
    const nextIndex = trackIndex + 1
    setIsLoading(true)

    try {
      // CASE A: Next index already exists in the current tracks array
      if (nextIndex < tracks.length) {
        const nextUrl = tracks[nextIndex].url

        // 1) Preload metadata for the next URL
        await preloadAudio(nextUrl)

        // 2) Pause the old audio so that our single audioRef does not overlap
        audioRef.current.pause()

        // 3) Update sharedState to bump trackIndex, reset progress, and set play/pause based on active player
        setSharedState((prev) => ({
          ...prev,
          trackIndex: nextIndex,
          trackProgress: 0,
          isPlaying: isActivePlayer,
        }))
      } else {
        // CASE B: We're at the end of the existing array, so fetch a fresh track via TRPC
        let data
        try {
          const result = await refetch()
          data = result.data
          if (!data) {
            throw new Error('No data returned from server')
          }
        } catch (fetchErr) {
          showToastNotification('error', {
            message: 'Failed to fetch next track',
            description: String(fetchErr),
          })
          return
        }

        // Construct the new AudioTrack object from TRPC response
        const newTrack: AudioTrack = {
          title: data.name ?? 'CreativeOS Tunes',
          artist: data.artist ?? 'Unknown Artist',
          url: data.url,
          artwork: '../../../assets/artwork.jpg',
        }

        try {
          // 1) Preload metadata for this newly fetched URL
          await preloadAudio(newTrack.url)

          // 2) Append the new track to the shared tracks[] array
          setTracks((prevArr) => [...prevArr, newTrack])
        } catch (preloadErr) {
          showToastNotification('error', {
            message: 'Failed to preload new track',
            description: String(preloadErr),
          })
          return
        }

        // 3) Pause the old audio so our single audioRef remains paused before swapping
        audioRef.current.pause()

        // 4) Update sharedState to set trackIndex to the new last index, reset progress, set play/pause
        setSharedState((prev) => ({
          ...prev,
          trackIndex: nextIndex,
          trackProgress: 0,
          isPlaying: isActivePlayer,
        }))
      }
    } catch (err) {
      showToastNotification('error', {
        message: 'Error advancing to next track',
        description: String(err),
      })
    } finally {
      setIsLoading(false)
    }
  }, [trackIndex, tracks, refetch, setTracks, setSharedState, isActivePlayer, isLoading])

  // --------------------------------------------------------------------------
  // 14) Go to the Previous Track
  // --------------------------------------------------------------------------

  /**
   * toPrevTrack: If trackIndex > 0, simply decrement trackIndex and reset progress,
   * then set sharedState.isPlaying = isActivePlayer. If trackIndex is already 0,
   * show a warning toast because there's no previous track.
   */
  const toPrevTrack = useCallback(() => {
    if (isLoading) {
      return
    }
    if (trackIndex === 0) {
      showToastNotification('warning', {
        message: "You're at the first track",
        description: '',
      })
    } else {
      // Pause our single audioRef first to avoid overlap
      audioRef.current.pause()

      setSharedState((prev) => ({
        ...prev,
        trackIndex: prev.trackIndex - 1,
        trackProgress: 0,
        isPlaying: isActivePlayer,
      }))
    }
  }, [trackIndex, setSharedState, isActivePlayer, isLoading])

  // --------------------------------------------------------------------------
  // 15) Handle Scrubbing (User Drags the Slider)
  // --------------------------------------------------------------------------

  /**
   * onScrub: Called whenever the user moves the progress slider.
   *   • Clear the interval so we don't fight with the user.
   *   • Seek the audioRef to the new time.
   *   • Write the new time into sharedState.trackProgress so both tabs sync.
   *   • Reset the hide timer so the UI stays expanded while dragging.
   *
   * We implement a simple throttle to avoid flooding localStorage writes.
   */
  const lastScrubTimeRef = useRef<number>(0)
  const onScrub = useCallback(
    (value: number) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      audioRef.current.currentTime = value

      const now = Date.now()
      if (now - lastScrubTimeRef.current > 200) {
        setSharedState((prev) => ({
          ...prev,
          trackProgress: value,
        }))
        lastScrubTimeRef.current = now
      }

      resetHideTimer()
    },
    [resetHideTimer, setSharedState],
  )

  // --------------------------------------------------------------------------
  // 16) Handle Scrub End (User Releases the Slider)
  // --------------------------------------------------------------------------

  /**
   * onScrubEnd: Called when the user releases the scrubber.
   *   • If audio was paused, toggle play (so it resumes).
   *   • Otherwise, restart the interval timer to keep updating progress.
   *   • In all cases, reset the hide timer so the UI stays expanded briefly.
   */
  const onScrubEnd = useCallback(() => {
    if (!isPlaying) {
      // If it was paused, now we want to start playing again
      setSharedState((prev) => ({
        ...prev,
        isPlaying: true,
      }))
    } else {
      // If it was already playing, just restart the timer
      startTimer()
    }
    resetHideTimer()
  }, [isPlaying, resetHideTimer, setSharedState, startTimer])

  // --------------------------------------------------------------------------
  // 17) Handle User Interaction to Expand UI
  // --------------------------------------------------------------------------

  /**
   * handleInteraction: When user hovers over or clicks on the mini‐player,
   * expand it and reset the auto‐hide timer.
   */
  const handleInteraction = useCallback(() => {
    setIsExpanded(true)
    resetHideTimer()
  }, [resetHideTimer])

  // --------------------------------------------------------------------------
  // 18) Load & Play the Current Track whenever trackIndex or isPlaying Changes
  // --------------------------------------------------------------------------

  useEffect(() => {
    /**
     * loadAudio:
     *   • Pause any existing audio playback.
     *   • Update our single <audio> element's `src` to the new track URL (audioSrc).
     *   • Seek to the saved trackProgress.
     *   • Overwrite the `onloadedmetadata` handler so old listeners don't accumulate.
     *   • If isPlaying && isActivePlayer, call play() and start the timer.
     *   • If this is the popout, send a message to the parent so it can show a “Now Playing” toast.
     */
    const loadAudio = async () => {
      if (!audioSrc) return

      // 1) Pause any existing playback on the old element
      audioRef.current.pause()

      // 2) Change the Audio's source and reset its currentTime
      audioRef.current.src = audioSrc
      audioRef.current.currentTime = trackProgress

      // 3) Overwrite any previous metadata listener, to avoid stacking
      audioRef.current.onloadedmetadata = async () => {
        if (isPlaying && isActivePlayer) {
          try {
            await audioRef.current.play()
            startTimer()
          } catch (e) {
            showToastNotification('error', {
              message: 'Audio playback error',
              description: String(e),
            })
          }
        }
      }

      // 4) If this is the popout window, let the parent know which track just started
      if (isPopout && window.opener) {
        window.opener.postMessage(
          {
            type: 'AUDIO_TRACK_CHANGED',
            payload: { title: currentTitle, artist: currentArtist },
          },
          '*',
        )
      }
    }

    loadAudio()
    // We omit certain deps (like resetHideTimer) on purpose:
    // Only re-run when trackIndex, audioSrc, isPlaying, or popout‐related flags change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIndex, audioSrc, isPlaying, isPopout, isPopoutOpen, startTimer, isActivePlayer, currentTitle, currentArtist])

  // --------------------------------------------------------------------------
  // 19) Respond to isPlaying Changes (Play/Pause) when Active
  // --------------------------------------------------------------------------

  useEffect(() => {
    // If this window is not the active player, do nothing
    if (!isActivePlayer) return

    if (isPlaying) {
      // If sharedState says “play” and we’re the active player, call play() and start timer
      const playAudio = async () => {
        try {
          await audioRef.current.play()
          startTimer()
        } catch (err) {
          showToastNotification('error', {
            message: 'Play failed',
            description: String(err),
          })
        }
      }
      playAudio()
    } else {
      // If sharedState says “pause”, then pause and clear the timer
      audioRef.current.pause()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // On cleanup (e.g., component unmount), clear any running interval
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isPlaying, isActivePlayer, startTimer])

  // --------------------------------------------------------------------------
  // 20) Sync Playback Position When Another Tab Updated trackProgress
  // --------------------------------------------------------------------------

  useEffect(() => {
    const localTime = audioRef.current.currentTime
    // If the difference between actual currentTime and shared trackProgress > 1s, resync
    if (Math.abs(localTime - trackProgress) > 1) {
      audioRef.current.currentTime = trackProgress
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackProgress])

  // --------------------------------------------------------------------------
  // 21) Listen for Popout→Parent Messages (to Show a Toast)
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isPopout) {
      // Only the parent installs this listener
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'AUDIO_TRACK_CHANGED') {
          const { title, artist } = event.data.payload
          showToastNotification('success', {
            message: `Playing: ${title}`,
            description: `By: ${artist}`,
          })
        }
      }
      window.addEventListener('message', handleMessage)
      return () => window.removeEventListener('message', handleMessage)
    }
  }, [isPopout])

  // --------------------------------------------------------------------------
  // 22) Popout Window Unload Handler (Cleanup)
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (isPopout) {
      // When the popout is about to close/navigate away, set isPopoutOpen = false
      // so the parent can resume playing if it was paused.
      const handleBeforeUnload = () => {
        setIsPopoutOpen(false)
      }
      window.addEventListener('beforeunload', handleBeforeUnload)
      return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isPopout, setIsPopoutOpen])

  // --------------------------------------------------------------------------
  // 23) Popout Initialization (Only Once)
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (isPopout && !didInit.current) {
      didInit.current = true
      // Mark that a popout is now open
      setIsPopoutOpen(true)
      // Immediately set isPlaying = true so the popout starts playing right away
      setSharedState((prev) => ({
        ...prev,
        isPlaying: true,
      }))
    }
  }, [isPopout, setIsPopoutOpen, setSharedState])

  // --------------------------------------------------------------------------
  // 24) Parent Detects Popout & Pauses
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isPopout && isPopoutOpen) {
      // If this is the parent (isPopout === false) and popout is open, pause parent audio
      audioRef.current.pause()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isPopoutOpen, isPopout])

  // --------------------------------------------------------------------------
  // 25) Cleanup on Unmount
  // --------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      // When the component unmounts (either parent or popout closes), pause audio and clear timers
      audioRef.current.pause()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      // Note: Popout unload handler is already handled above
    }
  }, [])

  // --------------------------------------------------------------------------
  // 26) Initial Track Fetch: Push First Track when randomMusic Arrives
  // --------------------------------------------------------------------------

  useEffect(() => {
    const pushFirstTrack = async () => {
      // Only do this if TRPC returned data AND we have no tracks yet
      if (randomMusic && tracks.length === 0) {
        const newTrack: AudioTrack = {
          title: randomMusic.name ?? 'CreativeOS Tunes',
          artist: randomMusic.artist ?? 'Unknown Artist',
          url: randomMusic.url,
          artwork: '../../../assets/artwork.jpg',
        }
        try {
          // Preload the metadata so playback is seamless
          await preloadAudio(newTrack.url)
          // Write the single-item array into sharedStorage
          setTracks([newTrack])
        } catch (err) {
          showToastNotification('error', {
            message: 'Failed to preload initial track',
            description: String(err),
          })
        } finally {
          setIsLoading(false)
        }
      }
    }
    pushFirstTrack()
    // Only re-run when randomMusic changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [randomMusic])

  // --------------------------------------------------------------------------
  // 27) Clear Spinner Once tracks[] Is Non-Empty
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (tracks.length > 0 && isLoading) {
      setIsLoading(false)
    }
  }, [tracks.length, isLoading])

  // --------------------------------------------------------------------------
  // 28) Preload Next Track in Background
  // --------------------------------------------------------------------------

  useEffect(() => {
    const idx = trackIndex + 1
    // If there is a “next” in the array, preload its metadata
    if (idx < tracks.length) {
      preloadAudio(tracks[idx].url).catch((e) => {
        console.error('Preload failed for track', idx, e)
      })
    }
  }, [trackIndex, tracks])

  // --------------------------------------------------------------------------
  // 29) Compute Clamped Progress Value for Slider
  // --------------------------------------------------------------------------

  /**
   * progressValue: If duration is known and > 0, clamp trackProgress to [0, duration].
   * Otherwise, show 0. This ensures the scrubber never shows a value > actual duration.
   */
  const progressValue = typeof duration === 'number' && duration > 0 ? Math.min(trackProgress, duration) : 0

  // --------------------------------------------------------------------------
  // 30) Function to Open the Popout Window
  // --------------------------------------------------------------------------

  /**
   * openPopout: Called from parent UI. It:
   *   • Opens a new browser window pointed at '/popout-audio'
   *   • Sets isPopoutOpen = true in sharedStorage
   *   • Pauses the parent’s audio and sets sharedState.isPlaying = false so popout becomes active
   *
   * We prevent multiple popouts by checking isPopoutOpen first.
   */
  const openPopout = () => {
    if (isPopoutOpen) {
      showToastNotification('default', {
        message: 'Popout window is already open',
        description: '',
      })
      return
    }
    const pop = window.open('/popout-audio', 'AudioPopout', 'width=350,height=100')
    if (pop) {
      setIsPopoutOpen(true)
      audioRef.current.pause()
      setSharedState((prev) => ({
        ...prev,
        isPlaying: false,
      }))
    } else {
      showToastNotification('error', {
        message: 'Failed to open popout window',
        description: '',
      })
    }
  }

  // --------------------------------------------------------------------------
  // 31) Return All Controls/Data Needed by Consumer Components
  // --------------------------------------------------------------------------

  return {
    containerRef, // can be attached to a <div> in parent UI
    isPopoutOpen, // whether popout is currently open
    isExpanded, // whether the mini‐player UI is expanded
    isLoading, // whether we’re preloading or have no tracks yet
    isFetching, // whether TRPC is currently fetching randomMusic
    playlist: tracks, // array of AudioTrack objects
    currentTrackIndex: trackIndex,
    currentTitle,
    currentArtist,
    currentArtwork,
    currentDuration: duration,
    currentProgress: progressValue,
    isPlaying, // shared “play/pause” flag
    handlePlayPause: (play: boolean) =>
      setSharedState((prev) => ({
        ...prev,
        isPlaying: play,
      })),
    toNextTrack,
    toPrevTrack,
    onScrub,
    onScrubEnd,
    handleInteraction,
    openPopout,
  }
}
